import { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Autocomplete,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import { useGetTeachersPaginatedQuery } from "../staff/staffApiSlice";
import {
  useAddTeacherToClassroomMutation,
  useGetClassesQuery,
  useRemoveTeacherFromClassroomMutation,
} from "./classApiSlice";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import DeleteIcon from "@mui/icons-material/Delete";
import toast from "../../../helpers/toast";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";

function ClassManagers({ item, setItem }) {
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [focusedTeacher, setFocusedTeacher] = useState(null);

  const [teacherSearch, setTeacherSearch] = useState("");
  const { data: teachersData } = useGetTeachersPaginatedQuery({ page: 1, per_page: 10, q: teacherSearch });
  const teachers = teachersData?.teachers ?? [];
  const { data: classrooms } = useGetClassesQuery("all"); //to get updated list of classrooms
  const [addTeacherToClass, { isLoading: addTeacherToClassLoading }] =
    useAddTeacherToClassroomMutation();
  const [removeTeacher] = useRemoveTeacherFromClassroomMutation();

  useEffect(() => {
    //update only the managers list to avoid overwriting enriched subject data (teacher_name) from ClassSubjects
    if (!classrooms) return;
    const updatedClassroom = classrooms.find((c) => c.id === item?.id);
    if (!updatedClassroom) return;
    setItem((prev) => ({ ...prev, managers: updatedClassroom.managers }));
  }, [classrooms]);

  const classForm = useFormik({
    initialValues: {
      formMaster: "",
    },
    validationSchema: yup.object({
      formMaster: yup.string().required("Required"),
    }),
    onSubmit: async (values) => {
      const body = {
        class_Id: item.id,
        user_id: values.formMaster,
      };
      try {
        const response = await addTeacherToClass(body).unwrap();
        if (response.message) {
          toast.success("Teacher added successfully");
        } else {
          toast.error("Teacher not added");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  const handleRemoveTeacher = async () => {
    try {
      const body = {
        class_Id: item.id,
        user_id: focusedTeacher.id,
      };
      const response = await removeTeacher(body).unwrap();
      if (response.message) {
        toast.success(response.message);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  return (
    <StyledPaper>
      <CustomWarningDialog
        open={openWarningDialog}
        setOpen={setOpenWarningDialog}
        item={focusedTeacher}
        action={handleRemoveTeacher}
        message="are you sure you want to remove this teacher from this class?"
      />

      <Box className="paperHeader">
        <Typography variant="paperHeading">Class Form masters</Typography>
      </Box>
      <Box className="paperBody">
        <form onSubmit={classForm.handleSubmit}>
          <Grid container columnSpacing={1} rowSpacing={2}>
            <Grid item xs={12} md={8}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">
                  add form master
                </Typography>
                <Autocomplete
                  options={teachers}
                  getOptionLabel={(option) => option ? `${option.first_name} ${option.last_name}` : ""}
                  isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                  value={teachers.find(t => t.user_id === classForm.values.formMaster) || null}
                  onChange={(_, value) => classForm.setFieldValue("formMaster", value?.user_id || "")}
                  onInputChange={(_, value, reason) => { if (reason === "input") setTeacherSearch(value); }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder="Search teacher..."
                      error={classForm.touched.formMaster && Boolean(classForm.errors.formMaster)}
                    />
                  )}
                />
                <FormHelperText variant="error">
                  {classForm.touched.formMaster && classForm.errors.formMaster}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                mb: 0.5,
              }}
            >
              <StyledLoadingButton
                loading={addTeacherToClassLoading}
                type="submit"
              >
                Add
              </StyledLoadingButton>
            </Grid>
          </Grid>
        </form>
      </Box>
      <TableContainer sx={{ pb: 1 }}>
        <Table sx={{ width: "100%", boxShadow: "none" }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>name</StyledTableCell>
              <StyledTableCell align="right">remove</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {item?.managers?.map((manager) => (
              <StyledTableRow key={manager.user_id}>
                <StyledTableCell component="th" scope="row">
                  {manager?.profile?.first_name} {manager?.profile?.last_name} ({manager?.profile?.email || manager?.profile?.phone || "-"})
                </StyledTableCell>
                <StyledTableCell component="th" scope="row" align="right">
                  <DeleteIcon
                    className="tableCellLink"
                    onClick={() => {
                      setOpenWarningDialog(true);
                      setFocusedTeacher(manager);
                    }}
                  />
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </StyledPaper>
  );
}

export default ClassManagers;
